import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { brl } from "@/lib/format";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { Download, FileText } from "lucide-react";

const COLORS = ["#D94814", "#F59E0B", "#0EA5E9", "#10B981", "#8B5CF6"];

export default function Reports() {
  const [top, setTop] = useState([]);
  const [chn, setChn] = useState([]);
  const [dre, setDre] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/reports/top-products"), api.get("/reports/channels")]).then(([t, c]) => { setTop(t.data); setChn(c.data); });
    api.get("/reports/dre").then((r) => setDre(r.data)).catch(() => {});
  }, []);

  const download = (path, name) => {
    const t = localStorage.getItem("pg_token");
    fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-extrabold text-stone-900">Relatórios</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={() => download("/reports/export/orders.csv", "pedidos.csv")} data-testid="export-orders">
            <Download className="h-4 w-4" /> Pedidos CSV
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => download("/reports/export/orders.pdf", "pedidos.pdf")} data-testid="export-orders-pdf">
            <FileText className="h-4 w-4" /> Pedidos PDF
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => download("/reports/export/financial.csv", "financeiro.csv")} data-testid="export-financial">
            <Download className="h-4 w-4" /> Financeiro CSV
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => download("/reports/export/financial.pdf", "financeiro.pdf")} data-testid="export-financial-pdf">
            <FileText className="h-4 w-4" /> Financeiro PDF
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => download("/reports/export/top-products.csv", "top-produtos.csv")} data-testid="export-top">
            <Download className="h-4 w-4" /> Top produtos CSV
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => download("/reports/export/dre.pdf", "dre.pdf")} data-testid="export-dre-pdf">
            <FileText className="h-4 w-4" /> DRE PDF
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Top 10 produtos</h3>
          <div className="h-72"><ResponsiveContainer>
            <BarChart data={top} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
              <XAxis type="number" tickFormatter={(v) => `R$${v}`} fontSize={11} />
              <YAxis dataKey="name" type="category" width={130} fontSize={11} />
              <Tooltip formatter={(v) => brl(v)} />
              <Bar dataKey="revenue" fill="#D94814" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer></div>
        </CardContent></Card>
        <Card><CardContent className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Vendas por canal</h3>
          <div className="h-72"><ResponsiveContainer>
            <PieChart>
              <Pie data={chn} dataKey="total" nameKey="_id" cx="50%" cy="50%" outerRadius={90} label={(p) => p._id}>
                {chn.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => brl(v)} /><Legend />
            </PieChart>
          </ResponsiveContainer></div>
        </CardContent></Card>
      </div>

      {dre && (
        <Card><CardContent className="p-6" data-testid="dre-card">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-orange-600" />
            <h3 className="font-display font-bold text-lg">DRE gerencial</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
              <div className="text-xs uppercase text-emerald-700 font-semibold">Receita bruta</div>
              <div className="font-mono-num text-2xl font-extrabold text-emerald-700">{brl(dre.receita_bruta)}</div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4">
              <div className="text-xs uppercase text-rose-700 font-semibold">Despesas totais</div>
              <div className="font-mono-num text-2xl font-extrabold text-rose-700">{brl(dre.despesas_total)}</div>
            </div>
            <div className={`rounded-xl border p-4 ${dre.resultado_liquido >= 0 ? "border-orange-200 bg-orange-50/50" : "border-rose-300 bg-rose-50"}`}>
              <div className="text-xs uppercase font-semibold text-orange-700">Resultado líquido</div>
              <div className={`font-mono-num text-2xl font-extrabold ${dre.resultado_liquido >= 0 ? "text-orange-700" : "text-rose-700"}`}>{brl(dre.resultado_liquido)}</div>
            </div>
          </div>
          {dre.despesas_por_categoria?.length > 0 && (
            <div className="mt-6">
              <h4 className="font-display font-bold text-sm text-stone-700 mb-2">Despesas por categoria</h4>
              <div className="rounded-lg border border-stone-200 overflow-hidden">
                {dre.despesas_por_categoria.map((c) => (
                  <div key={c.category} className="flex justify-between px-4 py-2 border-b last:border-b-0 text-sm">
                    <span className="text-stone-700">{c.category}</span>
                    <span className="font-mono-num font-bold text-rose-600">{brl(c.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}
